import React from 'react';
import { useLocale } from '../../../contexts/LocaleContext.jsx';
import { useCountdown } from '../../../hooks/useCountdown.js';
import { COUNTDOWN_DAY_MS, countdownLabel } from '../../../_helpers/countdown.js';
import { leavingText } from '../../../_helpers/shopLeaving.js';
import '../../../styles/SkinPriceTag.css';

/** A skin's price for display: "Free" at 0, else US dollars. Display only; never sent anywhere. */
export const skinPriceLabel = (t, format, priceCents) => (priceCents === 0
  ? t('shop.free')
  : format.usd(priceCents / 100, { cents: 'auto' }));

/**
 * After a sale's end, the re-read is asked for again this often while the
 * item still shows the old end (the device clock a little ahead of the
 * server's), at most SALE_END_RETRIES more times.
 */
export const SALE_END_RETRY_MS = 5000;
export const SALE_END_RETRIES = 3;

/**
 * The clock of an item's sale (ENG-832): useCountdown on `sale.endsAt`, so
 * the item that shows the price can also turn its Get it off once the sale
 * has ended. `onSaleEnd()` is that item's re-read (the shop's, the try-on
 * offer's or the shelf's); the price is never flipped here. No sale, no timer.
 * `options` are more of useCountdown's (`secondsWithinMs`, for a sale that
 * ends as the item leaves the shop, ENG-876).
 */
export const useSaleClock = (sale, onSaleEnd, options = {}) => useCountdown(sale?.endsAt ?? null, {
  ...options,
  onEnd: onSaleEnd,
  repeatMs: SALE_END_RETRY_MS,
  maxRepeats: SALE_END_RETRIES,
});

/**
 * How a sale ends, in words: "Sale ended", "Ends in 3h 20m" (a live countdown
 * in its last 24 hours) or "Sale ends Dec 3"; and the same for a screen
 * reader ("sale ended", "ends in 3 hours", "ends Dec 3"). With
 * `windowEnds` (the item leaves the shop at the same moment, ENG-834) the
 * end says when it leaves instead, as SkinTimeLimit does (ENG-876):
 * "Leaving Apr 1, 2027", "Leaving in 05:12:09", "No longer available".
 */
export const saleEnd = (t, format, sale, clock, windowEnds = false) => {
  if (windowEnds) {
    const line = leavingText(t, format, sale.endsAt, { remainingMs: clock?.remainingMs, ended: Boolean(clock?.ended), seconds: clock?.seconds ?? false });
    return { text: line.text, spoken: line.spoken };
  }
  if (clock?.ended) return { text: t('shop.sale.ended'), spoken: t('shop.sale.ariaEnded') };
  const remaining = clock?.remainingMs ?? (Date.parse(sale.endsAt) - Date.now());
  if (remaining < COUNTDOWN_DAY_MS) {
    const endsAtMs = Date.parse(sale.endsAt);
    return {
      text: t('shop.sale.endsIn', { time: countdownLabel(t, format, remaining, { seconds: clock?.seconds ?? false }) }),
      spoken: t('shop.sale.ariaEndsIn', { time: format.relativeTime(endsAtMs, { now: endsAtMs - remaining, numeric: 'always' }) }),
    };
  }
  const date = format.date(sale.endsAt, 'monthDay');
  return { text: t('shop.sale.endsOn', { date }), spoken: t('shop.sale.ariaEndsOn', { date }) };
};

/**
 * An item's price, the same on every skin surface (ENG-832): the shop tile,
 * the try-on panel, the FX try-on panel and the shelf's locked items.
 *
 * - No sale: the one price, as before ("$1.99", "Free" at 0), in a span with
 *   `className` and `testId`.
 * - On sale (`sale` from normalizeSale): the regular price struck through,
 *   the sale price (`priceCents`, "Free" for a $0 sale), a Sale badge and its
 *   end ("Ends in 3h 20m", live, in the last 24 hours; else "Sale ends Dec 3";
 *   "Sale ended" once over, until the re-read lands). Screen readers get one
 *   sentence that doesn't rely on the strikethrough: "On sale: $0.99, regular
 *   price $1.99, ends in 3 hours".
 *
 * `clock` is the item's useSaleClock, owned by the item so its Get it can
 * turn off at the end too. `priceCents` is shown as the backend sent it.
 * `windowEnds`: the item's sale window ends with the sale (ENG-834), so this
 * end line is the only one shown: it says when the item leaves (ENG-876),
 * and once over it reads "No longer available".
 * `showEnd={false}` leaves the end line out of the visual tag (the shop's
 * drop cards show it in their header instead, ENG-894); screen readers still
 * hear it.
 */
const SkinPriceTag = ({ priceCents, sale = null, clock = null, windowEnds = false, className = '', testId = undefined, showEnd = true }) => {
  const { t, format } = useLocale();
  if (priceCents === null || priceCents === undefined) return null;
  const price = skinPriceLabel(t, format, priceCents);
  if (!sale) return <span className={className || undefined} data-testid={testId}>{price}</span>;

  const regular = skinPriceLabel(t, format, sale.regularPriceCents);
  const end = saleEnd(t, format, sale, clock, windowEnds);
  return (
    <span className={`skin-price-tag skin-price-tag--sale ${className}`.trim()} data-testid={testId} data-on-sale="true">
      <span className="skin-price-tag-sr" data-testid="skin-price-sr">
        {t('shop.sale.ariaLabel', { price, regular, ends: end.spoken })}
      </span>
      <span className="skin-price-tag-visual" aria-hidden="true">
        <s className="skin-price-regular" data-testid="skin-price-regular">{regular}</s>
        <span className="skin-price-sale" data-testid="skin-price-sale">{price}</span>
        <span className="skin-sale-badge" data-testid="skin-sale-badge">{t('shop.sale.badge')}</span>
        {showEnd && (
          <span
            className={`skin-sale-ends${clock?.ended ? ' skin-sale-ends--ended' : ''}`}
            data-testid="skin-sale-ends"
          >
            {end.text}
          </span>
        )}
      </span>
    </span>
  );
};

export default SkinPriceTag;
